import { buildAtlAttributionHeaders } from '@atlassian/search-client/atl-attribution';
import { fetchJiraFieldsQuery } from '@atlassian/search-client/hydration';
import {
	fetchJiraPRsQuery,
	parseJiraPRResponse,
} from '@atlassian/search-client/hydration/pr-fetch';
import type { IssueLinkedPRs } from '@atlassian/search-client/hydration/pr-types';
import { fetchTeamsBulk } from '@atlassian/search-client/teams';

import type { PrimaryProductKey } from '../../../common/constants/products';
import type { CommonAttributes, FilterSelectionType } from '../../../common/utils/events/types';

import type { JiraIssueStatusAndColorName } from './search-result/search-result-renderer/search-jira-issue';
import type { AutoAppliedFilterInfo } from './use-search-results-auto-filters';

export async function fetchAndUpdateTeamNames(
	cloudId: string | undefined,
	teamIds: string[],
	nodeIdMap: Record<string, string | null>,
	setTeamNames: React.Dispatch<React.SetStateAction<Record<string, string | null>>>,
	logError: (error: unknown) => void,
): Promise<void> {
	if (cloudId && teamIds.length > 0) {
		try {
			const { teams } = await fetchTeamsBulk({ siteId: cloudId, teamIds });
			const newTeamNames: Record<string, string> = {};
			for (let i = 0; i < teams.length; i++) {
				const team = teams[i];
				newTeamNames[team.id] = team.displayName;
			}
			setTeamNames((prevTeamNames) => {
				const updatedTeamNames = { ...prevTeamNames };
				for (const [nodeId, teamId] of Object.entries(nodeIdMap)) {
					updatedTeamNames[nodeId] = teamId ? newTeamNames[teamId] : null;
				}
				return updatedTeamNames;
			});
			return;
		} catch (e) {
			logError(e);
		}
	}
	// If fetching teams fails, we still want to update the team names to null
	setTeamNames((prevTeamNames) => {
		const updatedTeamNames = { ...prevTeamNames };
		for (const [nodeId] of Object.entries(nodeIdMap)) {
			updatedTeamNames[nodeId] = null;
		}
		return updatedTeamNames;
	});
}

export const fetchAndUpdateJiraFields = async (
	cloudId: string | undefined,
	issueKeys: string[],
	aggAbsoluteUrl: string | undefined,
	setJiraStatuses: React.Dispatch<
		React.SetStateAction<Record<string, JiraIssueStatusAndColorName | null>>
	>,
	logError: (error: unknown) => void,
	activationId?: string,
	product?: PrimaryProductKey,
): Promise<void> => {
	if (cloudId && issueKeys.length > 0) {
		try {
			const headers = buildAtlAttributionHeaders({
				cloudId,
				activationId,
				product,
			});
			const response = await fetchJiraFieldsQuery({
				variables: {
					issueKeys,
					cloudId,
				},
				aggAbsoluteUrl,
				headers,
			});
			const issuesByKey = response.data?.jira?.issuesByKey;
			setJiraStatuses((prevIssueStatuses) => {
				if (issuesByKey) {
					const issueStatuses: Record<string, JiraIssueStatusAndColorName | null> = {
						...prevIssueStatuses,
					};
					// Have seen the issuesByKey have [null] responses
					for (const issue of issuesByKey.filter((issue) => issue)) {
						const statusName = issue.statusField?.status?.name;
						const colorName = issue.statusField?.status?.statusCategory?.colorName;
						if (issue.id && statusName && colorName) {
							issueStatuses[issue.id] = {
								statusName,
								colorName,
							};
						}
					}
					return issueStatuses;
				}
				return prevIssueStatuses;
			});
			return;
		} catch (e) {
			logError(e);
		}
	}
	// If fetching Jira fields fails, we still want to update the issue statuses to null
	setJiraStatuses((prevIssueStatuses) => {
		const issueStatuses: Record<string, JiraIssueStatusAndColorName | null> = {
			...prevIssueStatuses,
		};
		for (const key of issueKeys) {
			issueStatuses[key] = null;
		}
		return issueStatuses;
	});
};

export const fetchAndUpdateJiraLinkedPRs = async (
	issueAris: string[],
	cloudId: string | undefined,
	aggAbsoluteUrl: string | undefined,
	setLinkedPRs: React.Dispatch<React.SetStateAction<IssueLinkedPRs>>,
	logError: (error: unknown) => void,
): Promise<void> => {
	if (!cloudId || issueAris.length === 0) {
		return;
	}

	try {
		const response = await fetchJiraPRsQuery({
			issueAris,
			aggAbsoluteUrl,
			cloudId,
		});

		const parsed = parseJiraPRResponse(response, issueAris);
		setLinkedPRs((prev) => ({
			...prev,
			...parsed,
		}));
	} catch (e) {
		logError(e);
		// On failure, set error entries so UI knows hydration attempted but failed
		setLinkedPRs((prev) => {
			const updated = { ...prev };
			for (const ari of issueAris) {
				updated[ari] = { status: 'error' };
			}
			return updated;
		});
	}
};

/**
 * Merges auto-applied filter info into commonAttributes.filters and filterSelections
 */
export const mergeAutoAppliedFilters = (
	commonAttributes: CommonAttributes,
	autoAppliedFilters: AutoAppliedFilterInfo[],
): CommonAttributes => {
	if (autoAppliedFilters.length === 0) {
		return commonAttributes;
	}

	// Get existing filter IDs to avoid duplicates
	const existingFilterIds = new Set(commonAttributes.filters);

	// Create filter selections for auto-applied filters
	const autoAppliedFilterSelections: FilterSelectionType[] = autoAppliedFilters
		.filter((af) => !existingFilterIds.has(af.filterId))
		.map((af) => ({
			filterId: af.filterId,
			value: af.value,
			isSmartDefault: false,
			wasAutoApplied: true,
		}));

	// Get new filter IDs to add
	const newFilterIds = autoAppliedFilters
		.filter((af) => !existingFilterIds.has(af.filterId))
		.map((af) => af.filterId);

	return {
		...commonAttributes,
		filters: [...commonAttributes.filters, ...newFilterIds],
		filterSelections: [...commonAttributes.filterSelections, ...autoAppliedFilterSelections],
	};
};
